import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getAuthMeApi, googleLoginApi, loginApi } from "../api/auth.api";
import type {
  GoogleLoginPayload,
  LoginPayload,
  TokenData,
  ProfileDetail,
} from "@/interface/auth";
import type { ApiResponse } from "@/interface/common";
import { useAuthToken } from "@/guards/useAuthToken";
import { authKeys, STALE_2_MIN } from "@/lib/queryKey";
import { ROUTES } from "@/utils";

export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToken } = useAuthToken();

  return useMutation<ApiResponse<TokenData>, Error, LoginPayload>({
    mutationFn: loginApi,
    onSuccess: async (response) => {
      const token = response?.data?.access;
      const refreshToken = response?.data?.refresh;
      if (token) {
        setToken(token, refreshToken);
      }
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      navigate(ROUTES.MY_PROFILE);
    },
  });
};

export const useGoogleLoginMutation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToken } = useAuthToken();

  return useMutation<ApiResponse<TokenData>, Error, GoogleLoginPayload>({
    mutationFn: googleLoginApi,
    onSuccess: async (response) => {
      const token = response?.data?.access;
      const refreshToken = response?.data?.refresh;
      if (token) {
        setToken(token, refreshToken);
      }
      await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      navigate(ROUTES.MY_PROFILE);
    },
  });
};

export const useCurrentUser = () => {
  const { isAuthenticated } = useAuthToken();

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const res = await getAuthMeApi();
      return res.data;
    },
    enabled: isAuthenticated,
    ...STALE_2_MIN,
  });
};
